import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import type { User } from "@supabase/supabase-js";
import { agencyCacheKeys, clearAgencySessionCache, readAgencyCacheEntry, shouldRetryAgencyRequest, writeAgencyCache } from "@/lib/agency-cache";

/* ------------------------------------------------------------------ */
/*  Shared agency subscription hook — React Query for deduplication   */
/* ------------------------------------------------------------------ */

export interface AgencySubInfo {
  hasSubscription: boolean | null;
  isTrialing: boolean;
  trialDaysLeft: number | null;
  trialEnd: string | null;
  agencyTier: string | null;
  cancelAtPeriodEnd: boolean;
  subscriptionEnd: string | null;
  billingInterval: string | null;
}

interface SubApiResponse {
  subscribed?: boolean;
  trialing?: boolean;
  trial_end?: string | null;
  agency_tier?: string | null;
  cancel_at_period_end?: boolean;
  subscription_end?: string | null;
  billing_interval?: string | null;
}

const EMPTY_AGENCY_SUB: AgencySubInfo = {
  hasSubscription: null,
  isTrialing: false,
  trialDaysLeft: null,
  trialEnd: null,
  agencyTier: null,
  cancelAtPeriodEnd: false,
  subscriptionEnd: null,
  billingInterval: null,
};

function mapSubResponse(data: SubApiResponse | null): AgencySubInfo {
  if (!data) {
    return { hasSubscription: null, isTrialing: false, trialDaysLeft: null, trialEnd: null, agencyTier: null, cancelAtPeriodEnd: false, subscriptionEnd: null, billingInterval: null };
  }
  const sub = data.subscribed === true;
  const trialing = data.trialing === true;
  const trialEnd = data.trial_end ?? null;
  const agencyTier = data.agency_tier ?? null;
  const cancelAtPeriodEnd = data.cancel_at_period_end === true;
  const subscriptionEnd = data.subscription_end ?? null;
  const billingInterval = data.billing_interval ?? null;
  let trialDaysLeft: number | null = null;
  if (trialing && trialEnd) {
    const diffMs = new Date(trialEnd).getTime() - Date.now();
    trialDaysLeft = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  }
  return { hasSubscription: sub, isTrialing: trialing, trialDaysLeft, trialEnd, agencyTier, cancelAtPeriodEnd, subscriptionEnd, billingInterval };
}

/**
 * Centralised hook for agency subscription status.
 * Uses React Query so the result is shared across all agency pages.
 */
export const useAgencySubscription = (user: User | null): AgencySubInfo & { subLoading: boolean } => {
  const cacheEntry = user ? readAgencyCacheEntry<AgencySubInfo>(agencyCacheKeys.subscription(user.id)) : null;

  const { data, isLoading } = useQuery({
    queryKey: ["agency_subscription", user?.id],
    queryFn: async (): Promise<AgencySubInfo> => {
      // Use getSession (cached/local) instead of loadValidatedSession to avoid
      // an extra network round-trip to getUser() on every subscription check.
      // The session is already validated during boot.
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return EMPTY_AGENCY_SUB;

      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (error) throw error;
      const mapped = mapSubResponse(data as SubApiResponse);
      writeAgencyCache(agencyCacheKeys.subscription(session.user.id), mapped);
      return mapped;
    },
    enabled: !!user,
    initialData: cacheEntry?.value,
    initialDataUpdatedAt: cacheEntry?.updatedAt,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: (failureCount, error) => shouldRetryAgencyRequest(failureCount, error, 1),
  });

  const resolved = data ?? EMPTY_AGENCY_SUB;

  return { ...resolved, subLoading: isLoading && !data };
};

/* ------------------------------------------------------------------ */
/*  Shared agency ID hook — reads from AuthContext profile (instant)   */
/* ------------------------------------------------------------------ */

/**
 * Returns the agency_id from the user's profile.
 * This is instant because the profile is already loaded by AuthContext,
 * eliminating the previous RPC waterfall.
 */
export const useAgencyId = (user: User | null) => {
  const { profile } = useAuth();
  const cachedAgencyId = profile?.agency_id ?? null;

  const { data, isLoading } = useQuery({
    queryKey: ["agency_id", user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from("agency_members")
        .select("agency_id")
        .eq("user_id", user.id)
        .not("joined_at", "is", null)
        .order("role", { ascending: true })
        .limit(1);

      if (error) throw error;

      return data?.[0]?.agency_id ?? cachedAgencyId;
    },
    enabled: !!user,
    initialData: cachedAgencyId,
    staleTime: 5 * 60 * 1000,
  });

  return { agencyId: data ?? null, isLoading: !!user && isLoading && !data };
};

/* ------------------------------------------------------------------ */
/*  Shared agency account hook — deduplicated across pages            */
/* ------------------------------------------------------------------ */

export interface AgencyAccountData {
  id: string;
  name: string;
  contact_email: string;
  website: string | null;
  logo_url: string | null;
  profile_photo_url: string | null;
  tier: string;
  plan: string;
  included_creators: number;
  extra_creators: number;
  included_managers: number;
  extra_managers: number;
  trial_ends_at: string | null;
  current_period_end: string | null;
  subscription_status: string | null;
  stripe_subscription_id: string | null;
  cancelled_at: string | null;
  payment_failed_at: string | null;
  billing_handoff_pending: boolean;
  locked: boolean;
  locked_at: string | null;
  locked_by_transfer_from: string | null;
}

export const useAgencyAccount = (agencyId: string | null) => {
  const cacheEntry = agencyId ? readAgencyCacheEntry<AgencyAccountData>(agencyCacheKeys.account(agencyId)) : null;

  return useQuery({
    queryKey: ["agency_account", agencyId],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Your session expired. Please sign in again.");
      }

      const { data, error } = await supabase.functions.invoke("agency-account", {
        body: { agencyId },
      });
      if (error) throw error;
      if (!data || typeof data !== "object" || "error" in data) {
        throw new Error((data as { error?: string } | null)?.error ?? "Failed to load agency account");
      }

      const account = data as AgencyAccountData;
      writeAgencyCache(agencyCacheKeys.account(agencyId!), account);
      return account;
    },
    enabled: !!agencyId,
    initialData: cacheEntry?.value,
    initialDataUpdatedAt: cacheEntry?.updatedAt,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: (failureCount, error) => shouldRetryAgencyRequest(failureCount, error, 1),
  });
};

/* ------------------------------------------------------------------ */
/*  Shared agency role hook — deduplicates role query across pages     */
/* ------------------------------------------------------------------ */

export type AgencyRole = "admin" | "manager" | "viewer";

/**
 * Returns the current user's role within the agency.
 * Skips the query when agencyId is null (e.g. demo mode).
 */
export const useAgencyRole = (agencyId: string | null) => {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ["agency_my_role", agencyId, user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("agency_members")
        .select("role")
        .eq("agency_id", agencyId!)
        .eq("user_id", user!.id)
        .not("joined_at", "is", null)
        .maybeSingle();
      // Return the actual role or null if no membership row found.
      // NEVER default to "viewer" — that misclassifies admins during loading
      // or transient query failures and shows them the staff-locked view.
      return (data?.role ?? null) as AgencyRole | null;
    },
    enabled: !!user && !!agencyId,
    staleTime: 5 * 60 * 1000,
  });

  return query.data ?? null;
};

/**
 * Same as useAgencyRole but also returns loading state so callers can avoid
 * rendering role-gated UI before the role has actually been resolved.
 */
export const useAgencyRoleResolved = (agencyId: string | null) => {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ["agency_my_role", agencyId, user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("agency_members")
        .select("role")
        .eq("agency_id", agencyId!)
        .eq("user_id", user!.id)
        .not("joined_at", "is", null)
        .maybeSingle();
      return (data?.role ?? null) as AgencyRole | null;
    },
    enabled: !!user && !!agencyId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    role: (query.data ?? null) as AgencyRole | null,
    isResolved: !!user && !!agencyId ? !query.isLoading && query.fetchStatus !== "fetching" || query.data !== undefined : true,
    isLoading: query.isLoading,
  };
};

/* ------------------------------------------------------------------ */
/*  Clear cache helper (call on sign-out)                             */
/* ------------------------------------------------------------------ */

export const clearAgencyCache = () => {
  clearAgencySessionCache();
};
